import excavator from "@/assets/equip-excavator.jpg";
import truck from "@/assets/equip-truck.jpg";
import tractor from "@/assets/equip-tractor.jpg";
import crane from "@/assets/equip-crane.jpg";
import generator from "@/assets/equip-generator.jpg";
import forklift from "@/assets/equip-forklift.jpg";
import mixer from "@/assets/equip-mixer.jpg";
import airline from "@/assets/elra_airline.png";
import boat from "@/assets/elra_boat.png";
import ship from "@/assets/elra_ship.png";
import type { EquipmentSpecificationEntry } from "@/lib/equipmentSpecifications";

export interface Equipment {
  id: string;
  title: string;
  image: string;
  galleryMedia?: string[];
  availability?: string;
  pricePerDay: string;
  monthlyRate?: string;
  annualRate?: string;
  purchasePrice?: string;
  location: string;
  category: string;
  subcategory?: string;
  categoryPath?: string;
  specifications?: string;
  specificationRows?: EquipmentSpecificationEntry[];
  isBnpl: boolean;
  isFeatured: boolean;
  views: number;
  equipmentId?: string;
  registrationStatus?: "Registered" | "Not registered";
  verificationStatus?: "Verified" | "Not verified" | "Pending Review";
  ownerName?: string;
  ownerUserKey?: string;
  contactName?: string;
  contactPhone?: string;
  provideOperator?: boolean;
  operatorPolicy?: "owner-only" | "external-allowed";
  escrowAvailable?: boolean;
  leaseOptions?: Array<"Lease to use" | "Lease to own" | "Outright purchase" | "Buy now pay later">;
  downpaymentPercentage?: string;
  interactionStats?: {
    totalActionClicks: number;
    buyNowClicks: number;
    leaseNowClicks: number;
    bnplClicks: number;
  };
  isPreorder?: boolean | string;
  estimatedDeliveryDate?: string;
  deliveredFrom?: string;
  isInsured?: boolean | string;
  insuranceId?: string;
  insuranceDocument?: string;
}

export interface ServiceProvider {
  id: string;
  name: string;
  serviceTitle?: string;
  role: string;
  serviceType: string;
  location: string;
  rating: number;
  reviews: number;
  views?: number;
  avatar: string;
  image: string;
  verified: boolean;
  featured: boolean;
  userKey?: string;
  contactName?: string;
  contactPhone?: string;
  contactWhatsapp?: string;
  galleryMedia?: string[];
}

export interface Category {
  id: string;
  name: string;
  icon: string;
  count: number;
}

export const categories: Category[] = [
  { id: "1", name: "Agriculture & Mining", icon: "🌾", count: 187 },
  { id: "2", name: "Transport & Logistic", icon: "🚛", count: 143 },
  { id: "3", name: "Manufacturing & Industrial", icon: "🏭", count: 121 },
  { id: "4", name: "Construction & Infrastruture", icon: "🏗️", count: 234 },
  { id: "5", name: "Maritime & Shipping", icon: "🚢", count: 54 },
  { id: "6", name: "Energy & Power", icon: "⚡", count: 98 },
  { id: "7", name: "Aviation", icon: "✈️", count: 26 },
  { id: "8", name: "HealthCare & Fitness", icon: "🏥", count: 42 },
  { id: "9", name: "Telecom & Communication", icon: "📡", count: 39 },
  { id: "10", name: "Office & ICT", icon: "💻", count: 63 },
  { id: "11", name: "Green Energy", icon: "🌱", count: 33 },
  { id: "12", name: "Others", icon: "📦", count: 19 },
];

export const serviceProviderCategories = [
  "Logistic Firm",
  "Insurance Firm",
  "Lease Finance Firm",
  "Equipment Operators & Drivers",
  "Repair Engineer",
  "Spare Part Merchant",
  "OEMs Marchant",
  "Merchants",
  "Artisans",
];

export const serviceProviderCategoryMeta = [
  { name: "Logistic Firm", icon: "🚚", count: 56 },
  { name: "Insurance Firm", icon: "🛡️", count: 23 },
  { name: "Lease Finance Firm", icon: "💳", count: 31 },
  { name: "Equipment Operators & Drivers", icon: "👷", count: 134 },
  { name: "Repair Engineer", icon: "🔧", count: 89 },
  { name: "Spare Part Merchant", icon: "🧰", count: 18 },
  { name: "OEMs Marchant", icon: "🏭", count: 12 },
  { name: "Merchants", icon: "🏪", count: 0 },
  { name: "Artisans", icon: "🔨", count: 0 },
];

export const featuredEquipment: Equipment[] = [];

export const serviceProviders: ServiceProvider[] = [];

export const sidebarCategories = [
  { name: "Agriculture & Mining", subcategories: ["Tractors", "Harvesters", "Excavators", "Rock Drills"] },
  {
    name: "Transport & Logistic",
    subcategories: [
      "Pickup Trucks",
      "SUVs & Utility Vehicles",
      "Sedans",
      "Buses",
      "Mini Buses",
      "Coaster Buses",
      "Vans",
      "Cargo Vans",
      "Delivery Vans",
      "Mini Trucks",
      "Flatbed Trucks",
      "Dump Trucks",
      "Box Trucks",
      "Refrigerated Trucks",
      "Tipper Trucks",
      "Tow Trucks",
      "Tanker Trucks",
      "Crane Trucks",
      "Cement Mixer Trucks",
      "Car Carrier Trucks",
      "Semi Trucks",
      "Heavy Duty Trucks",
      "Trailers",
      "Flatbed Trailers",
      "Low Bed Trailers",
      "Container Trailers",
      "Tanker Trailers",
      "Motorcycles",
      "Dispatch Bikes",
      "Tricycles (Keke)",
      "Electric Vehicles",
      "Forklifts",
      "Reach Stackers",
      "Agricultural Transport Vehicles",
      "Construction Transport Vehicles",
      "Utility Vehicles",
      "Emergency Vehicles",
      "Ambulances",
      "Fire Trucks",
      "Mobile Workshop Vehicles",
    ],
  },
  { name: "Manufacturing & Industrial", subcategories: ["Compressors", "Welding Equipment", "Packaging Lines"] },
  { name: "Construction & Infrastruture", subcategories: ["Excavators", "Cranes", "Loaders", "Concrete Mixers"] },
  { name: "Maritime & Shipping", subcategories: ["Boats (Speedboats, Yachts, Fishing Boats)", "Ships (Cargo Ships, Container Vessels, Oil Tankers, Cruise Ships)", "Marine Engines", "Port Handling"] },
  { name: "Energy & Power", subcategories: ["Generators", "Transformers", "Solar Backup"] },
  { name: "Aviation", subcategories: ["Airlines (Commercial, Private Jets, Cargo Planes)", "Helicopters", "Ground Support", "Airport Logistics"] },
  { name: "HealthCare & Fitness", subcategories: ["Diagnostic Equipment", "Fitness Systems"] },
  { name: "Telecom & Communication", subcategories: ["Towers", "Transmitters", "Fiber Tools"] },
  { name: "Office & ICT", subcategories: ["Servers", "Networking", "Printers"] },
  { name: "Green Energy", subcategories: ["Solar Panels", "Wind Systems", "Inverters"] },
  { name: "Fashion & Tailoring", subcategories: [] },
  { name: "Beauty", subcategories: ["Hair Making & Styling", "Makeup", "Others"] },
  { name: "Carpentry", subcategories: [] },
  { name: "Safety Equipment", subcategories: [] },
  { name: "Others", subcategories: ["Special Requests", "Custom Assets"] },
];
